import { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Eye, ChevronLeft, ChevronRight, Zap, Star } from 'lucide-react';
import { sfxClick, sfxHover, sfxPowerUp, sfxWhoosh } from '@/lib/soundFX';

interface Employee {
  id: string;
  name: string;
  roleTitle: string;
  departmentId: string;
  bio?: string;
  skills?: string[];
  price?: number;
  photoUrl?: string;
  rating?: number;
  reviewsCount?: number;
}

interface FeaturedAgentProps {
  employees: Employee[];
  onView: (emp: Employee) => void;
  onAddToCart: (emp: Employee) => void;
}

const DEPT_COLORS: Record<string, string> = {
  'executive-operations': '#00A6EB',
  'sales-marketing': '#FF0055',
  'finance-accounting': '#FFB800',
  'technology-development': '#00FF88',
  'hr-talent': '#A855F7',
  'customer-success': '#FF6B00',
  'content-creative': '#FF1493',
  'data-analytics': '#00FFFF',
  'ecommerce-amazon': '#FF8C00',
  'legal-compliance': '#7CFC00',
};

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0,166,235';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

export default function FeaturedAgent({ employees, onView, onAddToCart }: FeaturedAgentProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [glowX, setGlowX] = useState(50);
  const [glowY, setGlowY] = useState(50);

  // Pick 5 featured agents (highest rated or first 5)
  const featured = employees.slice(0, 5);
  if (featured.length === 0) return null;

  const agent = featured[activeIdx];
  const color = DEPT_COLORS[agent?.departmentId] || '#00A6EB';
  const rgb = hexToRgb(color);

  const goTo = useCallback((idx: number) => {
    if (transitioning) return;
    setTransitioning(true);
    sfxWhoosh();
    setTimeout(() => {
      setActiveIdx(idx);
      setTransitioning(false);
    }, 300);
  }, [transitioning]);

  // Auto-rotate every 8 seconds
  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx(i => (i + 1) % featured.length);
    }, 8000);
    return () => clearInterval(t);
  }, [featured.length]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setGlowX(((e.clientX - rect.left) / rect.width) * 100);
    setGlowY(((e.clientY - rect.top) / rect.height) * 100);
  };

  if (!agent) return null;
  const price = agent.price ?? 297;

  return (
    <section style={{
      position: 'relative',
      padding: '4rem 0',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, rgba(7,9,18,0) 0%, rgba(7,9,18,0.8) 100%)',
    }}>
      {/* Section header */}
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <div style={{
          fontFamily: 'Share Tech Mono, monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.4em',
          color: 'rgba(0,166,235,0.6)',
          textTransform: 'uppercase',
          marginBottom: '0.5rem',
        }}>◈ ELITE AGENT SPOTLIGHT ◈</div>
        <h2 style={{
          fontFamily: 'Orbitron, monospace',
          fontWeight: 900,
          fontSize: 'clamp(1.4rem, 3vw, 2.2rem)',
          background: 'linear-gradient(135deg, #E8F4FF 0%, #00A6EB 50%, #FF0055 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          margin: 0,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>FEATURED AGENTS</h2>
      </div>

      {/* Main spotlight card */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 1.5rem' }}>
        <div
          onMouseMove={handleMouseMove}
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: 0,
            background: `linear-gradient(135deg, rgba(13,17,35,0.97), rgba(20,27,45,0.95))`,
            border: `1px solid rgba(${rgb}, 0.35)`,
            borderRadius: 6,
            overflow: 'hidden',
            boxShadow: `0 0 60px rgba(${rgb}, 0.12), 0 0 120px rgba(${rgb}, 0.04), 0 30px 60px rgba(0,0,0,0.5)`,
            opacity: transitioning ? 0 : 1,
            transform: transitioning ? 'scale(0.98)' : 'scale(1)',
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
          }}
        >
          {/* Mouse glow */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
            background: `radial-gradient(circle at ${glowX}% ${glowY}%, rgba(${rgb}, 0.08) 0%, transparent 50%)`,
          }} />

          {/* Left: large photo */}
          <div style={{
            position: 'relative',
            backgroundImage: agent.photoUrl
              ? `url(${agent.photoUrl})`
              : `linear-gradient(135deg, rgba(${rgb}, 0.2), rgba(${rgb}, 0.05))`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            minHeight: 360,
          }}>
            {/* Gradient overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(90deg, transparent 60%, rgba(13,17,35,0.95) 100%), linear-gradient(180deg, transparent 50%, rgba(7,9,18,0.6) 100%)`,
            }} />
            {/* Top-left badge */}
            <div style={{
              position: 'absolute', top: 16, left: 16, zIndex: 2,
              fontFamily: 'Orbitron, monospace',
              fontSize: '0.5rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: color,
              background: `rgba(${rgb}, 0.12)`,
              border: `1px solid rgba(${rgb}, 0.4)`,
              padding: '4px 10px',
              textTransform: 'uppercase',
            }}>◈ FEATURED</div>
            {/* Rating */}
            <div style={{
              position: 'absolute', bottom: 16, left: 16, zIndex: 2,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={10} fill={s <= Math.round(agent.rating ?? 5) ? '#FFB800' : 'transparent'} color='#FFB800' />
              ))}
              <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.55rem', color: 'rgba(232,244,255,0.5)', marginLeft: 4 }}>
                ({agent.reviewsCount ?? 0} reviews)
              </span>
            </div>
          </div>

          {/* Right: info */}
          <div style={{ padding: '2rem 2rem 2rem 1.5rem', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              {/* Top accent */}
              <div style={{ height: 2, background: `linear-gradient(90deg, ${color}, transparent)`, marginBottom: '1.5rem' }} />
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.25em',
                color: `rgba(${rgb}, 0.7)`,
                textTransform: 'uppercase',
                marginBottom: '0.4rem',
              }}>{agent.departmentId?.replace(/-/g, ' ').toUpperCase()}</div>
              <h3 style={{
                fontFamily: 'Orbitron, monospace',
                fontWeight: 900,
                fontSize: 'clamp(1.1rem, 2vw, 1.6rem)',
                color: '#E8F4FF',
                margin: '0 0 0.3rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                textShadow: `0 0 20px rgba(${rgb}, 0.3)`,
              }}>{agent.name}</h3>
              <div style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.7rem',
                color: color,
                marginBottom: '1rem',
                letterSpacing: '0.08em',
              }}>{agent.roleTitle}</div>
              <p style={{
                fontFamily: 'Share Tech Mono, monospace',
                fontSize: '0.62rem',
                color: 'rgba(232,244,255,0.55)',
                lineHeight: 1.7,
                margin: '0 0 1.2rem',
              }}>{agent.bio?.slice(0, 180)}{(agent.bio?.length ?? 0) > 180 ? '...' : ''}</p>
              {/* Skills */}
              {agent.skills && agent.skills.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '1.5rem' }}>
                  {agent.skills.slice(0, 5).map((sk, i) => (
                    <span key={i} style={{
                      fontFamily: 'Share Tech Mono, monospace',
                      fontSize: '0.5rem',
                      letterSpacing: '0.1em',
                      color: color,
                      background: `rgba(${rgb}, 0.08)`,
                      border: `1px solid rgba(${rgb}, 0.25)`,
                      padding: '3px 8px',
                      textTransform: 'uppercase',
                    }}>{sk}</span>
                  ))}
                </div>
              )}
            </div>
            {/* Price + CTA */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={{
                  fontFamily: 'Orbitron, monospace',
                  fontWeight: 900,
                  fontSize: '1.6rem',
                  color: '#FFB800',
                  textShadow: '0 0 20px rgba(255,184,0,0.5)',
                }}>${price.toLocaleString()}</div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem', color: 'rgba(232,244,255,0.3)', letterSpacing: '0.15em' }}>/DEPLOYMENT LICENSE</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => { sfxClick(); onView(agent); }}
                  onMouseEnter={() => sfxHover()}
                  style={{
                    fontFamily: 'Orbitron, monospace',
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    letterSpacing: '0.15em',
                    padding: '10px 18px',
                    background: 'rgba(0,166,235,0.1)',
                    border: '1px solid rgba(0,166,235,0.4)',
                    color: '#00A6EB',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s ease',
                    clipPath: 'polygon(6px 0%, 100% 0%, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0% 100%, 0% 6px)',
                  }}
                >
                  <Eye size={12} />
                  DOSSIER
                </button>
                <button
                  onClick={() => { sfxPowerUp(); onAddToCart(agent); }}
                  onMouseEnter={() => sfxHover()}
                  style={{
                    fontFamily: 'Orbitron, monospace',
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    letterSpacing: '0.15em',
                    padding: '10px 20px',
                    background: `linear-gradient(135deg, rgba(${rgb}, 0.25), rgba(${rgb}, 0.1))`,
                    border: `1px solid rgba(${rgb}, 0.6)`,
                    color: color,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: `0 0 20px rgba(${rgb}, 0.2)`,
                    transition: 'all 0.2s ease',
                    clipPath: 'polygon(6px 0%, 100% 0%, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0% 100%, 0% 6px)',
                  }}
                >
                  <Zap size={12} />
                  RECRUIT NOW
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation dots + arrows */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: '1.5rem' }}>
          <button
            onClick={() => goTo((activeIdx - 1 + featured.length) % featured.length)}
            onMouseEnter={() => sfxHover()}
            style={{ background: 'none', border: '1px solid rgba(0,166,235,0.3)', color: '#00A6EB', cursor: 'pointer', padding: '6px 8px', display: 'flex', alignItems: 'center', borderRadius: 2 }}
          ><ChevronLeft size={14} /></button>
          {featured.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              onMouseEnter={() => sfxHover()}
              style={{
                width: i === activeIdx ? 24 : 8,
                height: 8,
                borderRadius: 4,
                background: i === activeIdx ? '#00A6EB' : 'rgba(0,166,235,0.25)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: i === activeIdx ? '0 0 10px rgba(0,166,235,0.6)' : 'none',
                padding: 0,
              }}
            />
          ))}
          <button
            onClick={() => goTo((activeIdx + 1) % featured.length)}
            onMouseEnter={() => sfxHover()}
            style={{ background: 'none', border: '1px solid rgba(0,166,235,0.3)', color: '#00A6EB', cursor: 'pointer', padding: '6px 8px', display: 'flex', alignItems: 'center', borderRadius: 2 }}
          ><ChevronRight size={14} /></button>
        </div>
      </div>
    </section>
  );
}
